import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Autocomplete,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { API_URL } from "../../utils/apiUtils";
import debounce from "lodash/debounce"; // Debounce utility from lodash
import {
  getTodayPigmyCollectionsService,
  updatePigmyCollectionsService,
} from "../../services/pigmyService";
import { toast } from "react-toastify";

const CollectPigmyModal = ({ open, onClose, onSubmit }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pigmy, setPigmy] = useState({});
  const token = sessionStorage.getItem("accessToken");
  const date = new Date().toISOString().split("T")[0];
  const [isSubmit, setIsSubmit] = useState(false);

  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    const day = String(date.getDate()).padStart(2, "0"); // Ensure 2 digits
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  };
  // Fetch options from API
  const fetchOptions = async (query) => {
    if (!query) return; // Don't make an API call if the query is empty
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/pigmy/search-pigmy`, {
        params: { query },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      console.log(response.data.customers);
      setOptions(response.data.customers);
    } catch (error) {
      console.error("Error fetching options:", error);
    }
    setLoading(false);
  };

  // Debounce the fetchOptions API call to avoid excessive requests
  const debouncedFetchOptions = useCallback(
    debounce((query) => {
      fetchOptions(query);
    }, 300), // Delay the call by 300ms
    []
  );

  // Form validation schema
  const validationSchema = Yup.object().shape({
    pigmy: Yup.string().required("Required"),
    collectionAmount: Yup.number()
      .required("Required")
      .positive("Amount must be positive"),
    collectionDate: Yup.date().required("Required"),
    dueDate: Yup.date().required("Required"),
  });

  const handleAutocompleteChange = async (event, newValue, setFieldValue) => {
    try {
      setFieldValue("pigmy", newValue ? newValue._id : "");
      const fetchPigmy = await getTodayPigmyCollectionsService(newValue._id);
      setPigmy(fetchPigmy);
      setFieldValue("frequency", fetchPigmy?.pigmy?.frequency);
      setFieldValue("agentName", fetchPigmy?.agent?.userName);
      setFieldValue(
        "collectionAmount",
        fetchPigmy?.collectionAmount - fetchPigmy?.paidAmount
      );
      console.log(fetchPigmy?.dueDate);
      const dueDate = formatDate(fetchPigmy?.dueDate);
      setFieldValue("dueDate", dueDate);
    } catch (error) {
      toast.error(error.message);
    }

    //setFieldValue("collectionDate", fetchPigmy?.dueDate);
  };

  useEffect(() => {
    // Reset Formik values when modal is closed
    if (!open) {
      setOptions([]); // Reset options when modal is closed
    }
  }, [open]);

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle style={{ color: "red" }}>Pigmy Collection</DialogTitle>
      <Formik
        initialValues={{
          pigmy: "", // Store selected pigmy ID here
          agentName: "",
          frequency: "",
          collectionAmount: "",
          collectionDate: date,
          dueDate: null,
        }}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          setIsSubmit(true);
          const updatePigmyCollection = await updatePigmyCollectionsService(
            pigmy?._id,
            values?.collectionDate,
            values?.collectionAmount
          );
          toast.success("Pigmy collected successfully");
          onClose(); // Pass Formik values to the submit handler
          setIsSubmit(false);
        }}
      >
        {({ values, errors, touched, setFieldValue, isSubmitting }) => (
          <Form>
            <DialogContent dividers>
              <Grid container spacing={2}>
                {/* Customer Field */}
                <Grid item xs={12} sm={12}>
                  <Typography variant="body1" gutterBottom>
                    Customer Name/Pigmy Code/Phone Number
                  </Typography>
                  <Autocomplete
                    value={
                      options.find((option) => option._id === values.pigmy) ||
                      null
                    }
                    onChange={(event, newValue) => {
                      handleAutocompleteChange(event, newValue, setFieldValue);
                    }}
                    onInputChange={(event, newInputValue) => {
                      // Only fetch options if the input value changes due to typing
                      if (event && event.type === "change") {
                        debouncedFetchOptions(newInputValue); // Call debounced fetch
                      }
                    }}
                    options={options}
                    getOptionLabel={(option) =>
                      `${option.user.userName} (${option.pigmyCode}) (${option.user.primaryPhone?.contactValue})` ||
                      ""
                    }
                    loading={loading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        size="small"
                        error={Boolean(touched.pigmy && errors.pigmy)}
                        helperText={touched.pigmy && errors.pigmy}
                      />
                    )}
                  />
                </Grid>

                {/* Agent Name Field */}
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" gutterBottom>
                    Agent Name
                  </Typography>
                  <Field
                    name="agentName"
                    as={TextField}
                    fullWidth
                    size="small"
                    InputProps={{
                      readOnly: true, // Makes the field read-only
                    }}
                    error={Boolean(touched.agentName && errors.agentName)}
                    helperText={touched.agentName && errors.agentName}
                  />
                </Grid>

                {/* Frequency Field */}
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" gutterBottom>
                    Frequency
                  </Typography>
                  <Field
                    name="frequency"
                    as={TextField}
                    fullWidth
                    size="small"
                    InputProps={{
                      readOnly: true, // Makes the field read-only
                    }}
                    error={Boolean(touched.frequency && errors.frequency)}
                    helperText={touched.frequency && errors.frequency}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" gutterBottom>
                    Due Date
                  </Typography>
                  <Field
                    name="dueDate"
                    as={TextField}
                    fullWidth
                    size="small"
                    InputProps={{
                      readOnly: true, // Makes the field read-only
                    }}
                    error={Boolean(touched.frequency && errors.frequency)}
                    helperText={touched.frequency && errors.frequency}
                  />
                </Grid>

                {/* Collection Amount Field */}
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" gutterBottom>
                    Collection Amount
                  </Typography>
                  <Field
                    name="collectionAmount"
                    as={TextField}
                    fullWidth
                    size="small"
                    error={
                      touched.collectionAmount &&
                      Boolean(errors.collectionAmount)
                    }
                    helperText={
                      touched.collectionAmount && errors.collectionAmount
                    }
                  />
                </Grid>

                {/* Collection Date Field */}
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" gutterBottom>
                    Collection Date
                  </Typography>
                  <TextField
                    type="date"
                    value={values.collectionDate || ""}
                    onChange={(e) =>
                      setFieldValue("collectionDate", e.target.value)
                    }
                    fullWidth
                    size="small"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={
                      touched.collectionDate && Boolean(errors.collectionDate)
                    }
                    helperText={touched.collectionDate && errors.collectionDate}
                    InputProps={{
                      readOnly: true, // Makes the field read-only
                    }}
                  />
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button onClick={onClose} color="secondary">
                Cancel
              </Button>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={isSubmit}
              >
                Submit
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default CollectPigmyModal;
